import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Award, Users, Heart, MessageCircle, ArrowRight } from "lucide-react";
import { BUSINESS, IMG } from "@/data/site";
import Seo from "@/components/Seo";

const wa = (n) => `https://wa.me/91${n}?text=${encodeURIComponent("Hello Chakri Travels, I want to book a car rental service.")}`;

const About = () => {
  useEffect(() => {
    document.title = "About Chakri Travels — Andhra Pradesh's Premium Travel Partner";
  }, []);
  return (
    <main>
      <Seo
        title="About Chakri Travels — A Decade of Premium Journeys in Andhra Pradesh"
        description="A homegrown Andhra Pradesh travel brand serving Kakinada, Rajamahendravaram, Narasapuram & Amalapuram since 2014. Verified chauffeurs, premium fleet, 4.9★ rated."
        path="/about"
      />
      {/* Hero */}
      <section className="relative isolate flex min-h-[60svh] items-center overflow-hidden">
        <img src={IMG.hero} alt="Chakri Travels" className="absolute inset-0 -z-10 h-full w-full object-cover" />
        <div className="absolute inset-0 -z-10 bg-[var(--royal-deep)]/40" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[var(--royal-deep)]/90 via-[var(--royal-deep)]/75 to-[var(--royal)]/60" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/65 via-transparent to-black/30" />
        <div className="mx-auto w-full max-w-7xl px-5 pt-32 pb-20 lg:px-8 lg:pt-40">
          <div className="max-w-3xl reveal">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/60 glass-dark px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--gold)]">
              About Us
            </div>
            <h1 className="font-display text-3xl font-bold leading-[1.1] text-white sm:text-5xl lg:text-6xl hero-text-shadow">
              A decade of <span className="text-gold-on-image">premium journeys</span> across Andhra Pradesh
            </h1>
            <p className="mt-6 max-w-2xl text-base font-medium text-white sm:text-lg hero-text-shadow-soft">
              Born in the heart of the Godavari region, Chakri Travels is a homegrown Andhra Pradesh brand built around one simple promise — make every ride feel extraordinary.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--royal-deep)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]"></span> Our Story
            </div>
            <h2 className="font-display text-3xl font-bold text-[var(--royal-deep)] sm:text-5xl">
              Andhra Pradesh roots, <span className="text-gradient-gold">five-star standards</span>
            </h2>
            <p className="mt-5 text-base text-muted-foreground">
              What began as a small fleet serving local families in Kakinada has grown into one of coastal AP's most loved premium travel brands. Today, Chakri Travels operates across Kakinada, Rajamahendravaram, Narasapuram, Amalapuram — moving thousands of guests each year for weddings, business, airport transfers, and unforgettable Konaseema getaways.
            </p>
            <p className="mt-4 text-base text-muted-foreground">
              We are obsessed with the details — the polish on the doorframe, the chilled bottle of water, the chauffeur who knows when to converse and when to let the road sing. That obsession is why guests come back, generation after generation.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/services" className="inline-flex items-center gap-2 rounded-full gradient-gold px-6 py-3 text-sm font-semibold text-[var(--royal-deep)] shadow-lg shadow-[var(--gold)]/30 hover:-translate-y-0.5 transition-transform">
                Our services <ArrowRight size={14} />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-[var(--royal)] px-6 py-3 text-sm font-semibold text-white hover:-translate-y-0.5 transition-transform">
                Talk to us <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { I: Heart, t: "Our Mission", d: "Deliver effortlessly luxurious travel experiences that celebrate the beauty and warmth of Andhra Pradesh." },
              { I: Award, t: "Our Vision", d: "Become South India's most trusted name in premium chauffeur-driven travel." },
              { I: Users, t: "Professional Drivers", d: "Background-verified, hospitality-trained chauffeurs who treat every guest like family." },
              { I: ShieldCheck, t: "Safe & Insured", d: "Every vehicle is fully insured, GPS-tracked and serviced on a strict schedule." },
            ].map((c) => (
              <div key={c.t} className="rounded-2xl bg-white p-6 ring-1 ring-[var(--royal)]/10 shadow-[0_10px_30px_-15px_rgba(15,23,80,0.18)]">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl gradient-royal text-white">
                  <c.I size={20} />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-[var(--royal-deep)]">{c.t}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[linear-gradient(180deg,white,#f4f6fb)]">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
          {[
            { n: "12+", l: "Years of service" },
            { n: "50+", l: "Premium vehicles" },
            { n: "10,000+", l: "Happy travellers" },
            { n: "4.9★", l: "Average rating" },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <div className="font-display text-3xl sm:text-5xl font-bold text-gradient-gold">{s.n}</div>
              <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[var(--muted-foreground)]">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why guests trust us */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--royal-deep)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]"></span> Why guests trust us
          </div>
          <h2 className="font-display text-3xl font-bold text-[var(--royal-deep)] sm:text-5xl">
            Customer satisfaction is our only <span className="text-gradient-gold">KPI</span>
          </h2>
          <p className="mt-4 text-base text-muted-foreground">
            From the first call to the last drop-off, every touchpoint is engineered for comfort, safety and quiet luxury — the Chakri way.
          </p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-3">
          {[
            { t: "Award-winning", d: "Recognised among AP's top-rated travel companies." },
            { t: "People-first", d: "Real humans, real care — at any hour of the day." },
            { t: "Safety promise", d: "Sanitised cabins, verified drivers, zero compromise." },
          ].map((c) => (
            <div key={c.t} className="rounded-2xl bg-white p-6 ring-1 ring-[var(--royal)]/10 shadow-[0_10px_30px_-15px_rgba(15,23,80,0.18)] hover:-translate-y-1 transition-transform">
              <h3 className="font-display text-lg font-semibold text-[var(--royal-deep)]">{c.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <a href={wa(BUSINESS.primaryWhatsApp)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-[#25D366]/30 hover:-translate-y-0.5 transition-transform">
            <MessageCircle size={16} /> Chat on WhatsApp
          </a>
        </div>
      </section>
    </main>
  );
};

export default About;
